/**
 * ComparisonView Clipboard Utilities
 *
 * Functions for copying comparison data to clipboard.
 */

import type { FieldMetadata } from '@/services/metadataService'
import type { SectionGroup, ComparisonLabels } from '../types'
import { formatValueForText, formatRecordName } from './formatters'

/**
 * Format comparison data as plain text for clipboard.
 *
 * @example Output:
 * ```
 * COMPARISON SUMMARY
 * ==================
 * Current: Widget Pricing v3 (draft)
 * Revised: Widget Pricing v2 (active)
 *
 * DIFFERENCES (3 fields changed)
 * ------------------------------
 *
 * [Pricing]
 *   price: $42.00 -> $45.00 (+$3.00, +7.1%)
 *   cost: $28.00 -> $30.00 (+$2.00, +7.1%)
 *
 * [Metadata]
 *   effectiveFrom: Jan 1, 2025 -> Feb 1, 2025
 * ```
 */
export function formatComparisonAsText(
  leftRecord: Record<string, unknown> | null,
  rightRecord: Record<string, unknown> | null,
  sections: SectionGroup[],
  fields: Record<string, FieldMetadata>,
  labels: ComparisonLabels
): string {
  const lines: string[] = []

  // Header
  lines.push('COMPARISON SUMMARY')
  lines.push('==================')

  // Record info
  const leftState = leftRecord?.lifecycleState as string | undefined
  const rightState = rightRecord?.lifecycleState as string | undefined
  const leftVersion = leftRecord?.lifecycleVersion
  const rightVersion = rightRecord?.lifecycleVersion

  let leftLabel = formatRecordName(leftRecord)
  if (leftVersion != null) leftLabel += ` v${leftVersion}`
  if (leftState) leftLabel += ` (${leftState})`

  let rightLabel = formatRecordName(rightRecord)
  if (rightVersion != null) rightLabel += ` v${rightVersion}`
  if (rightState) rightLabel += ` (${rightState})`

  lines.push(`${labels.left}: ${leftLabel}`)
  lines.push(`${labels.right}: ${rightLabel}`)
  lines.push('')

  // Count total changes
  const totalChanges = sections.reduce((sum, s) => sum + s.changedCount, 0)

  if (totalChanges === 0) {
    lines.push('NO DIFFERENCES')
    lines.push('--------------')
    lines.push('The records are identical.')
  } else {
    lines.push(`DIFFERENCES (${totalChanges} field${totalChanges !== 1 ? 's' : ''} changed)`)
    lines.push('------------------------------')
    lines.push('')

    // Format each section
    for (const section of sections) {
      const changedFields = section.fields.filter(f => f.isDifferent)
      if (changedFields.length === 0) continue

      lines.push(`[${section.title}]`)

      for (const diff of changedFields) {
        const fieldMeta = fields[diff.fieldKey]
        const fieldTitle = fieldMeta?.title || diff.fieldKey

        const leftValue = formatValueForText(diff.leftValue, fieldMeta)
        const rightValue = formatValueForText(diff.rightValue, fieldMeta)

        let line = `  ${fieldTitle}: ${leftValue} -> ${rightValue}`

        // Add delta if numeric
        if (diff.numericDelta !== undefined && diff.numericDelta !== 0) {
          const prefix = diff.numericDelta > 0 ? '+' : ''
          line += ` (${prefix}${diff.numericDelta.toFixed(2)}`
          if (diff.percentDelta !== undefined) {
            const percentPrefix = diff.percentDelta > 0 ? '+' : ''
            line += `, ${percentPrefix}${diff.percentDelta.toFixed(1)}%`
          }
          line += ')'
        }

        lines.push(line)
      }

      lines.push('')
    }
  }

  // Footer
  lines.push('')
  lines.push(`Generated: ${new Date().toLocaleString()}`)

  return lines.join('\n')
}

/**
 * Copy text to clipboard with fallback for older browsers.
 *
 * @returns Promise that resolves when copy is successful
 */
export async function copyTextToClipboard(text: string): Promise<void> {
  // Try modern clipboard API first
  if (navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }

  // Fallback for older browsers
  const textArea = document.createElement('textarea')
  textArea.value = text
  textArea.style.position = 'fixed'
  textArea.style.left = '-999999px'
  textArea.style.top = '-999999px'
  document.body.appendChild(textArea)
  textArea.focus()
  textArea.select()

  try {
    document.execCommand('copy')
  } finally {
    document.body.removeChild(textArea)
  }
}
